import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Button } from "@/frontend/components/ui/button";
import { Input } from "@/frontend/components/ui/input";
import { Separator } from "@/frontend/components/ui/separator";
import { Loader2, Webhook } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/frontend/components/ui/dialog";
import { Field, FieldLabel, FieldGroup } from "@/frontend/components/ui/field";
import { Route } from "@/frontend/routes/dashboard/settings";
import { docsUrl } from "@/lib/docs";
import { useWebhooksQuery } from "@/frontend/queries/dashboard/settings";
import { useCreateWebhookMutation } from "@/frontend/mutations/dashboard/settings";
import {
  MAX_ENDPOINTS,
  WEBHOOK_EVENT_TYPES,
  type EventType,
} from "@/frontend/types/dashboard/settings/webhooks";
import { CopyField } from "./CopyField";
import { EventSelector } from "./EventSelector";
import { WebhookCard } from "./WebhookCard";
import { WebhookDetailView } from "./WebhookDetailView";

export function WebhooksSection() {
  const { webhookId } = Route.useSearch();
  const navigate = useNavigate();

  const { data: endpoints, isLoading } = useWebhooksQuery();

  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const [newEvents, setNewEvents] = useState<EventType[]>([...WEBHOOK_EVENT_TYPES]);

  const createWebhook = useCreateWebhookMutation();
  const createdEndpoint = createWebhook.data ?? null;

  if (webhookId) {
    return <WebhookDetailView webhookId={webhookId} />;
  }

  const list = endpoints ?? [];
  const atCapacity = list.length >= MAX_ENDPOINTS;

  const resetCreate = () => {
    setShowCreate(false);
    setNewName("");
    setNewUrl("");
    setNewEvents([...WEBHOOK_EVENT_TYPES]);
    createWebhook.reset();
  };

  const submitCreate = () => {
    createWebhook.mutate({
      url: newUrl.trim(),
      events: newEvents,
      description: newName.trim() || undefined,
    });
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-semibold">Webhooks</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Register HTTPS endpoints to receive event notifications.
          </p>
        </div>
        <Button
          onClick={() => { createWebhook.reset(); setShowCreate(true); }}
          disabled={atCapacity}
          title={atCapacity ? "Endpoint limit reached. Delete one to add another." : undefined}
        >
          <Webhook className="size-4 mr-2" />
          Add Endpoint
        </Button>
      </div>

      <Separator />

      <section>
        <h3 className="text-sm font-semibold mb-3">Configured Endpoints</h3>
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading endpoints...
          </div>
        ) : list.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Webhook className="h-8 w-8 mb-2 opacity-50" />
            <p className="text-sm">No webhook endpoints yet</p>
            <a
              href={docsUrl("/docs/webhooks/quickstart")}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-primary hover:underline mt-1"
            >
              Read the webhooks quickstart
            </a>
          </div>
        ) : (
          <div className="space-y-3">
            {list.map((ep) => (
              <WebhookCard
                key={ep.id}
                endpoint={ep}
                onClick={() =>
                  navigate({
                    to: "/dashboard/settings",
                    search: { section: "webhooks", webhookId: ep.id, destinationId: undefined, provider: "" },
                  })
                }
              />
            ))}
          </div>
        )}
        {atCapacity && (
          <p className="text-xs text-muted-foreground mt-3">
            You've reached the maximum number of endpoints. Delete one before adding another.
          </p>
        )}
      </section>

      {/* Create endpoint dialog */}
      <Dialog open={showCreate} onOpenChange={(open) => { if (!open) resetCreate(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{createdEndpoint ? "Endpoint created" : "Add webhook endpoint"}</DialogTitle>
            <DialogDescription>
              {createdEndpoint
                ? "Copy your signing secret now. You won't be able to see it again."
                : "Register an HTTPS URL and choose which events to receive."}
            </DialogDescription>
          </DialogHeader>
          {createdEndpoint ? (
            <CopyField label="Signing secret" value={createdEndpoint.secret} />
          ) : (
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="webhook-name">Name</FieldLabel>
                <Input
                  id="webhook-name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Production webhook"
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="webhook-url">Endpoint</FieldLabel>
                <Input
                  id="webhook-url"
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                  placeholder="https://api.yourapp.com/webhooks/medblocks"
                />
              </Field>
              <Field>
                <FieldLabel>Events</FieldLabel>
                <EventSelector selected={newEvents} onChange={setNewEvents} />
              </Field>
              {createWebhook.isError && (
                <p className="text-sm text-destructive">{createWebhook.error.message}</p>
              )}
            </FieldGroup>
          )}
          <DialogFooter>
            {createdEndpoint ? (
              <Button onClick={resetCreate}>Done</Button>
            ) : (
              <>
                <Button variant="outline" onClick={resetCreate}>Cancel</Button>
                <Button
                  onClick={submitCreate}
                  disabled={createWebhook.isPending || !newUrl.trim() || newEvents.length === 0}
                >
                  {createWebhook.isPending ? (
                    <>
                      <Loader2 className="size-4 mr-2 animate-spin" />
                      Creating…
                    </>
                  ) : (
                    "Create"
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}